import { adminFetch } from "./_client";
import type { PlatformConfig, AuditLogEntry, AnalyticsData, AnalyticsBusinessOverview, BookingVerificationMatrix, BookingSnapshotAuditResponse, BookingSnapshotReconcilePreviewResponse, BookingSnapshotReconcileApplyResponse, CronHealthResponse, BusinessHeartbeatResponse, BusinessHeartbeatDetailResponse, GeoCitationQuery, GeoCitationOverview, GeoCitationResult, CountryEntry, CountryConfigUpdate, NotificationAuditEntry, FunnelAnalytics, OtpFunnelAnalytics, OperationalReportResult, RentalSummaryFilters, RentalSummaryResult } from "./types";


export function fetchPlatformConfig(token: string) {
  return adminFetch<{ configs: PlatformConfig[] }>("/admin/config", token);
}


export function fetchBookingVerificationMatrix(token: string) {
  return adminFetch<{ matrix: BookingVerificationMatrix; configId: number | null; key: string }>("/admin/booking-verification", token);
}


export function updateBookingVerificationMatrix(token: string, matrix: BookingVerificationMatrix) {
  return adminFetch<{ success: boolean; matrix: BookingVerificationMatrix; configId: number | null }>("/admin/booking-verification", token, {
    method: "PUT",
    body: JSON.stringify({ matrix }),
  });
}


export function updatePlatformConfig(token: string, id: number, value: string, valueJson?: Record<string, unknown>) {
  return adminFetch<{ success: boolean }>(`/admin/config/${id}`, token, {
    method: "PUT",
    body: JSON.stringify({ value, value_json: valueJson }),
  });
}


export function fetchAuditLog(token: string, page = 1, entityType?: string, action?: string, changedBy?: string, dateFrom?: string, dateTo?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (entityType) params.set("entity_type", entityType);
  if (action) params.set("action", action);
  if (changedBy) params.set("changed_by", changedBy);
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  return adminFetch<{ logs: AuditLogEntry[]; total: number; page: number; limit: number }>(`/admin/audit?${params}`, token);
}


export function fetchAnalytics(
  token: string,
  params?: { period?: string; from?: string; to?: string; compare?: string },
) {
  const qs = new URLSearchParams();
  if (params?.period) qs.set("period", params.period);
  if (params?.from) qs.set("from", params.from);
  if (params?.to) qs.set("to", params.to);
  if (params?.compare) qs.set("compare", params.compare);
  const query = qs.toString();
  return adminFetch<AnalyticsData>(`/admin/analytics${query ? `?${query}` : ""}`, token);
}

export function fetchAnalyticsBusinessOverview(token: string) {
  return adminFetch<AnalyticsBusinessOverview>("/admin/analytics/overview", token);
}


export function fetchBookingSnapshotAudit(token: string, days = 30) {
  return adminFetch<BookingSnapshotAuditResponse>(
    `/admin/booking-snapshot-audit?days=${days}`,
    token,
  );
}


// Task #3351 — preview (dry-run) the reconcile for one (trip, class). Safe
// to call with audit.view; writes nothing.
export function previewBookingSnapshotReconcile(
  token: string,
  tripId: number,
  mismatchClass: string,
) {
  return adminFetch<BookingSnapshotReconcilePreviewResponse>(
    `/admin/booking-snapshot-audit/${tripId}/reconcile`,
    token,
    { method: "POST", body: JSON.stringify({ mismatch_class: mismatchClass, dry_run: true }) },
  );
}


// Task #3351 — apply the reconcile. Requires trips.financial_override and,
// for money-affecting classes, step-up re-auth (handled by adminFetch).
export function applyBookingSnapshotReconcile(
  token: string,
  tripId: number,
  mismatchClass: string,
) {
  return adminFetch<BookingSnapshotReconcileApplyResponse>(
    `/admin/booking-snapshot-audit/${tripId}/reconcile`,
    token,
    { method: "POST", body: JSON.stringify({ mismatch_class: mismatchClass }) },
  );
}


export function fetchCronHealth(token: string) {
  return adminFetch<CronHealthResponse>("/admin/cron-health", token);
}


export function fetchBusinessHeartbeat(token: string) {
  return adminFetch<BusinessHeartbeatResponse>("/admin/business-heartbeat", token);
}


export function fetchBusinessHeartbeatDetail(
  token: string,
  key: string,
  limit = 10,
) {
  return adminFetch<BusinessHeartbeatDetailResponse>(
    `/admin/business-heartbeat/${encodeURIComponent(key)}/detail?limit=${limit}`,
    token,
  );
}


export function fetchGeoCitationOverview(token: string, days = 30) {
  return adminFetch<GeoCitationOverview>(`/admin/geo-citations/overview?days=${days}`, token);
}


export function fetchGeoCitationQueries(token: string) {
  return adminFetch<{ queries: GeoCitationQuery[] }>("/admin/geo-citations/queries", token);
}


export function patchGeoCitationQuery(token: string, id: string, patch: { active?: boolean; notes?: string }) {
  return adminFetch<{ ok: true }>(`/admin/geo-citations/queries/${encodeURIComponent(id)}`, token, {
    method: "POST",
    body: JSON.stringify(patch),
  });
}


export function fetchGeoCitationResults(token: string, opts: { queryId?: string; engine?: string; days?: number; limit?: number } = {}) {
  const params = new URLSearchParams();
  if (opts.queryId) params.set("queryId", opts.queryId);
  if (opts.engine) params.set("engine", opts.engine);
  if (opts.days) params.set("days", String(opts.days));
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString();
  return adminFetch<{ results: GeoCitationResult[]; windowDays: number }>(
    `/admin/geo-citations/results${qs ? `?${qs}` : ""}`,
    token,
  );
}


export function recordGeoCitationManual(
  token: string,
  body: { queryId: string; engine: string; answerText?: string; citations?: string[]; bacCited?: boolean },
) {
  return adminFetch<{ ok: true }>("/admin/geo-citations/manual", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


export function fetchGeoCitationDigest(token: string, days = 30) {
  return adminFetch<{ markdown: string; windowDays: number; currentRate: number; previousRate: number; delta: number }>(
    `/admin/geo-citations/digest?days=${days}`,
    token,
  );
}


export function fetchCountries(token: string) {
  return adminFetch<{ countries: CountryEntry[] }>("/admin/countries", token);
}


export function toggleCountry(token: string, code: string) {
  return adminFetch<{ code: string; enabled: boolean }>(`/admin/countries/${code}/toggle`, token, { method: "PUT" });
}


export function updateCountryConfig(token: string, code: string, config: CountryConfigUpdate) {
  return adminFetch<{ success: boolean; code: string }>(`/admin/countries/${code}/config`, token, {
    method: "PUT",
    body: JSON.stringify(config),
  });
}


export function fetchNotificationAudit(token: string, page = 1, type?: string, channel?: string, userId?: string) {
  const params = new URLSearchParams({ page: String(page) });
  if (type) params.set("type", type);
  if (channel) params.set("channel", channel);
  if (userId) params.set("user_id", userId);
  return adminFetch<{ entries: NotificationAuditEntry[]; total: number }>(`/admin/notifications/audit?${params}`, token);
}


export function fetchFunnelAnalytics(token: string, period = "30d") {
  return adminFetch<FunnelAnalytics>(`/admin/analytics/funnel?period=${period}`, token);
}

export function fetchOtpFunnelAnalytics(token: string, period = "30d") {
  return adminFetch<OtpFunnelAnalytics>(`/admin/analytics/otp-funnel?period=${period}`, token);
}


export function fetchOperationalReport(token: string, reportId: string, params?: { period?: string; from?: string; to?: string }) {
  const qs = new URLSearchParams();
  if (params?.period) qs.set("period", params.period);
  if (params?.from) qs.set("from", params.from);
  if (params?.to) qs.set("to", params.to);
  return adminFetch<OperationalReportResult>(`/admin/reports/${reportId}?${qs}`, token);
}

export function fetchRentalSummary(token: string, filters: RentalSummaryFilters = {}, requestId?: number) {
  const qs = new URLSearchParams();
  if (filters.statuses?.length) qs.set("statuses", filters.statuses.join(","));
  if (filters.countries?.length) qs.set("countries", filters.countries.join(","));
  if (filters.paymentTypes?.length) qs.set("payment_types", filters.paymentTypes.join(","));
  if (filters.bookingTypes?.length) qs.set("booking_types", filters.bookingTypes.join(","));
  if (filters.bookedFrom) qs.set("booked_from", filters.bookedFrom);
  if (filters.bookedTo) qs.set("booked_to", filters.bookedTo);
  if (filters.rentalFrom) qs.set("rental_from", filters.rentalFrom);
  if (filters.rentalTo) qs.set("rental_to", filters.rentalTo);
  qs.set("page", String(filters.page || 1));
  qs.set("page_size", String(filters.pageSize || 25));
  if (requestId !== undefined) qs.set("_request", String(requestId));
  return adminFetch<RentalSummaryResult>(`/admin/reports/rental-summary?${qs}`, token);
}
